import React, { useState } from 'react';
import { Form, Input, Button, DatePicker, TimePicker, Select, InputNumber, Card, Row, Col, message } from 'antd';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { createPromoCode } from './CreatePromoCodeAction';
import ListPromoCodes from './ListPromoCode/ListPromoCodes';

const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const CreatePromoCode = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const amountType = Form.useWatch('amountType', form);
  
  const format = 'hh:mm a';

  const START_TIME = '12:00 am';
  const END_TIME = '11:59 pm';
  
  const countries = [
    // { code: "SG", name: "Singapore" },
    { code: "LK", name: "Sri Lanka" },
    // { code: "AUS", name: "Australia" }
  ];

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Format data for API
      const formattedData = {
        name: values.name,
        description: values.description,
        code: values.code,
        startDate: values.dateRange[0].format(),
        endDate: values.dateRange[1].format(),
        // startTime: values.startTime.format(format),
        startTime: START_TIME,
        endTime: END_TIME,
        usageLimit: values.usageLimit,
        individualUserUsageLimit: values.individualUserUsageLimit,
        discount: {
          amountType: values.amountType,
          amount: values.discountAmount,
          terms: values.terms || 0.0
        },
        dependencyParam: {
          country: "LK"
        }
      };
            
    await new Promise((resolve, reject) => {
      dispatch(createPromoCode(formattedData, resolve, reject));
    });      
    
      form.resetFields();
    } catch (error) {
      console.error('Error creating promo code:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-promo-code-container">
      <Card title="Create Promo Code" bordered={false}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            amountType: 'PERCENTAGE',
            terms: 0,
            // startTime: moment('01:00 am', format),
            // endTime: moment('11:30 pm', format)
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter promo code name' }]}
              >
                <Input placeholder="Enter promo code name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Code"
                rules={[
                  { required: true, message: 'Please enter promo code' },
                  { pattern: /^[A-Za-z0-9]+$/, message: 'Code must contain only letters and numbers' }
                ]}
              >
                <Input placeholder="Enter promo code (e.g. 1BT25)" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea rows={4} placeholder="Enter description for this promo code" />
          </Form.Item>
          
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="dateRange"
                label="Validity Period"
                rules={[{ required: true, message: 'Please select start and end dates' }]}
              >
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="country" 
                label="Country"
                initialValue="LK"
                rules={[{ required: true, message: 'Please select a country' }]}
              >
                <Select disabled placeholder="Select country">
                  {countries.map(country => (
                    <Option key={country.code} value={country.code}>{country.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          {/* <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="startTime"
                label="Start Time"
                rules={[{ required: true, message: 'Please select start time' }]}
              >
                <TimePicker format={format} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endTime"
                label="End Time"
                rules={[{ required: true, message: 'Please select end time' }]}
              >
                <TimePicker format={format} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row> */}
          
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="usageLimit"
                label="Usage Limit (Total)"
                rules={[
                  { required: true, message: 'Please enter usage limit' },
                  { type: 'number', min: 1, message: 'Usage limit must be at least 1' }
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="Maximum number of uses" min={1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="individualUserUsageLimit"
                label="Usage Limit (Per User)"
                rules={[
                  { required: true, message: 'Please enter individual usage limit' },
                  { type: 'number', min: 1, message: 'Individual limit must be at least 1' }
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="Maximum uses per user" min={1} />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name="amountType"
                label="Discount Type"
                rules={[{ required: true, message: 'Please select discount type' }]}
              >
                <Select>
                  <Option value="PERCENTAGE">Percentage</Option>
                  <Option value="VALUE">Fixed Amount</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="discountAmount"
                label="Discount Amount"
                rules={[
                  { required: true, message: 'Please enter discount amount' },
                  { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue('amountType') === 'PERCENTAGE' && value > 100) {
                        return Promise.reject('Percentage cannot exceed 100%');
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }} 
                  min={0.01}
                  precision={2}
                  placeholder="Enter amount"
                  formatter={(value) => {
                    return amountType === 'PERCENTAGE' ? `${value}%` : `${value}`;
                  }}
                  parser={(value) => {
                    return amountType === 'PERCENTAGE' ? value.replace('%', '') : value;
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="terms"
                label="Terms (Optional)"
                tooltip="Additional terms for the discount"
                initialValue={0}
              >
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="Terms value" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} style={{ marginRight: 16 }}>
              Create Promo Code
            </Button>
            <Button onClick={() => form.resetFields()}>
              Reset
            </Button>
          </Form.Item>
        </Form>
      </Card>
      <ListPromoCodes countries={countries} />

      
    </div>
  );
};

export default CreatePromoCode;